// src/components/auth/InactivityDetector.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Button, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';

const InactivityDetector = () => {
    const { user, logout } = useAuth();
    const [isModalVisible, setIsModalVisible] = useState(false);
    
    const warningTimer = useRef(null);
    const logoutTimer = useRef(null);
    const lastActivityTime = useRef(Date.now());
    const isFirstRender = useRef(true);

    // Función para extender la sesión
    const keepSessionAlive = async () => {
        try {
            await authService.getMe(); 
            console.log('Sesión extendida por actividad.');
            message.success('Tu sesión ha sido extendida.');
        } catch (error) {
            console.error('Error al extender la sesión:', error);
            logout('session-error');
        }
    };

    // Handler cuando el usuario elige quedarse conectado
    const handleStayLoggedIn = useCallback(() => {
        console.log('[InactivityDetector] Usuario eligió permanecer conectado');
        setIsModalVisible(false);
        
        // Extender sesión
        keepSessionAlive();
        
        // Actualizar tiempo de última actividad
        lastActivityTime.current = Date.now();
        
        // Reiniciar timers después de cerrar el modal
        // Usamos setTimeout para evitar problemas de estado
        setTimeout(() => {
            if (user) {
                setupTimers();
            }
        }, 100);
    }, [user]);

    // Handler para logout - NO depende de otras funciones
    const handleLogout = useCallback(() => {
        console.log('[InactivityDetector] Ejecutando logout por inactividad');
        setIsModalVisible(false);
        
        // Limpiar timers antes de hacer logout
        if (warningTimer.current) {
            clearTimeout(warningTimer.current);
            warningTimer.current = null;
        }
        if (logoutTimer.current) {
            clearTimeout(logoutTimer.current);
            logoutTimer.current = null;
        }
        
        // Hacer logout con razón
        logout('inactivity');
    }, [logout]);

    // Función para configurar los timers - NO es un callback para evitar dependencias circulares
    const setupTimers = () => {
        // Limpiar timers existentes
        if (warningTimer.current) {
            clearTimeout(warningTimer.current);
            warningTimer.current = null;
        }
        if (logoutTimer.current) {
            clearTimeout(logoutTimer.current);
            logoutTimer.current = null;
        }

        // Si no hay usuario, no configurar timers
        if (!user) {
            console.log('[InactivityDetector] No hay usuario, no se configuran timers');
            return;
        }

        const inactivityTimeoutMinutes = user.sessionInactivityTimeoutMinutes || 240;
        
        console.log(`[InactivityDetector] Configuración de inactividad: ${inactivityTimeoutMinutes} minutos`);
        
        // Si el timeout es menor a 3 minutos, no activar
        if (inactivityTimeoutMinutes < 3) {
            console.log('[InactivityDetector] Timeout muy corto (< 3 min), no se activa detección');
            return;
        }

        // Calcular tiempos
        const warningTime = (inactivityTimeoutMinutes - 2) * 60 * 1000;
        const logoutTime = inactivityTimeoutMinutes * 60 * 1000;

        console.log(`[InactivityDetector] Configurando timers:`);
        console.log(`  - Advertencia en: ${warningTime/60000} minutos`);
        console.log(`  - Logout en: ${logoutTime/60000} minutos`);

        // Configurar timer de advertencia
        warningTimer.current = setTimeout(() => {
            console.log('[InactivityDetector] Mostrando modal de advertencia');
            setIsModalVisible(true);
        }, warningTime);

        // Configurar timer de logout
        logoutTimer.current = setTimeout(() => {
            console.log('[InactivityDetector] Tiempo expirado, ejecutando logout');
            // Solo hacer logout si el modal no está visible (el usuario no respondió)
            if (isModalVisible) {
                handleLogout();
            }
        }, logoutTime);
    };

    // Handler para detectar actividad del usuario con throttling
    const handleUserActivity = useCallback(() => {
        // Si el modal está visible, no hacer nada
        if (isModalVisible) {
            return;
        }

        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTime.current;
        
        // Solo resetear si han pasado al menos 60 segundos desde la última actividad
        // Esto evita resetear constantemente con cada movimiento
        if (timeSinceLastActivity > 60000) { // 60 segundos
            console.log('[InactivityDetector] Actividad detectada después de 60 segundos, reiniciando timers');
            lastActivityTime.current = now;
            setupTimers();
        }
    }, [isModalVisible]);

    // Efecto principal para configurar la detección de inactividad
    useEffect(() => {
        // Si es el primer render, solo marcar como false y salir
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!user) {
            // Limpiar todo si no hay usuario
            console.log('[InactivityDetector] Usuario deslogueado, limpiando timers');
            if (warningTimer.current) {
                clearTimeout(warningTimer.current);
                warningTimer.current = null;
            }
            if (logoutTimer.current) {
                clearTimeout(logoutTimer.current);
                logoutTimer.current = null;
            }
            return;
        }

        console.log('[InactivityDetector] Usuario detectado, iniciando detección de inactividad');
        
        // Configurar timers iniciales
        setupTimers();

        // Eventos a monitorear (reducidos para evitar exceso de eventos)
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        
        // Agregar listeners
        events.forEach(event => {
            window.addEventListener(event, handleUserActivity);
        });

        // Cleanup
        return () => {
            console.log('[InactivityDetector] Limpiando listeners y timers');
            events.forEach(event => {
                window.removeEventListener(event, handleUserActivity);
            });
            if (warningTimer.current) {
                clearTimeout(warningTimer.current);
                warningTimer.current = null;
            }
            if (logoutTimer.current) {
                clearTimeout(logoutTimer.current);
                logoutTimer.current = null;
            }
        };
    }, [user, handleUserActivity]); // Solo depende de user y handleUserActivity

    // Footer del modal
    const modalFooter = [
        <Button key="logout" onClick={handleLogout}>
            Cerrar Sesión
        </Button>,
        <Button key="stay" type="primary" onClick={handleStayLoggedIn}>
            Permanecer Conectado
        </Button>,
    ];

    // Para debugging - mostrar estado actual
    useEffect(() => {
        if (user) {
            console.log('[InactivityDetector] Estado actual:', {
                usuario: user.username,
                tiempoInactividad: user.sessionInactivityTimeoutMinutes || 240,
                modalVisible: isModalVisible
            });
        }
    }, [user, isModalVisible]);

    return (
        <Modal
            title={
                <span>
                    <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                    Tu sesión está a punto de expirar
                </span>
            }
            open={isModalVisible}
            onOk={handleStayLoggedIn}
            onCancel={handleStayLoggedIn}
            footer={modalFooter}
            closable={false}
            maskClosable={false}
            centered
        >
            <p>Detectamos un periodo de inactividad. Por tu seguridad, cerraremos tu sesión en 2 minutos.</p>
            <p>¿Deseas continuar trabajando?</p>
        </Modal>
    );
};

export default InactivityDetector;